import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { ARCHIE_PANEL_WIDTH } from "../../theme/v2Tokens";
import { AuthContext } from "../../../context/AuthContext";
import {
  useStartArchieChat,
  useContinueArchieChat,
  useListArchieConversations,
  useLoadArchieConversation,
  useInvalidateArchieConversations,
} from "../../../hooks/useArchieChat";
import type {
  ArchieChatMessage,
  ArchieConversationSummary,
} from "../../../types/archie/archie";

interface ArchiePanelProps {
  open: boolean;
  onClose: () => void;
}

const TABS = ["Chat", "History"] as const;
type Tab = (typeof TABS)[number];

// ---------------------------------------------------------------------------
// Module-scope sub-component extracted to avoid defining components inside render
// ---------------------------------------------------------------------------

interface MarkdownLinkProps {
  href?: string;
  children?: React.ReactNode;
  onViewResults: (url: string) => void;
  [key: string]: unknown;
}

function MarkdownLink({ href, children, onViewResults, ...props }: MarkdownLinkProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (href?.includes("/customers/")) {
      e.preventDefault();
      onViewResults(href);
    }
  };
  return (
    <a
      {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      href={href}
      onClick={handleClick}
      style={{ color: "var(--archie-text)", textDecoration: "underline" }}
    >
      {children}
    </a>
  );
}

const STORAGE_KEYS = {
  conversationId: "archie_conversation_id",
  messages: "archie_messages",
  userId: "archie_user_id",
} as const;


const BOILERPLATE_PATTERNS = [
  /You can view these interactions by clicking the following link:\s*/gi,
  /You can view (?:these|the) results? by clicking the following link:\s*/gi,
  /Click the following link to view (?:these|the) (?:interactions|results):\s*/gi,
];

const SEARCH_URL_REGEX = /\/customers\/[^\s)\]"]+/;

function stripBoilerplate(text: string): string {
  let cleaned = text;
  for (const pattern of BOILERPLATE_PATTERNS) {
    cleaned = cleaned.replace(pattern, "");
  }
  // Also strip any bare URL that matches the searchUrl pattern at the end of text
  cleaned = cleaned.replace(/\s*https?:\/\/[^\s)\]"]*\/customers\/[^\s)\]"]*\s*$/g, "");
  return cleaned.trim();
}

function extractSearchUrl(text: string, responseUrl?: string): string | undefined {
  if (responseUrl) return responseUrl;
  const match = text.match(SEARCH_URL_REGEX);
  return match ? match[0] : undefined;
}

function makeFormatRelativeTime(t: (key: string, opts?: Record<string, unknown>) => string) {
  return function formatRelativeTime(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return t("archie:justNow");
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t("archie:minutesAgo", { count: diffMin });
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t("archie:hoursAgo", { count: diffHr });
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return t("archie:daysAgo", { count: diffDay });
    return new Date(dateStr).toLocaleDateString();
  };
}

function restoreSession(userId: string): {
  conversationId: string | null;
  messages: ArchieChatMessage[];
} {
  try {
    const storedUserId = sessionStorage.getItem(STORAGE_KEYS.userId);
    if (storedUserId !== userId) {
      sessionStorage.removeItem(STORAGE_KEYS.conversationId);
      sessionStorage.removeItem(STORAGE_KEYS.messages);
      sessionStorage.removeItem(STORAGE_KEYS.userId);
      return { conversationId: null, messages: [] };
    }
    const convId = sessionStorage.getItem(STORAGE_KEYS.conversationId);
    const msgs = sessionStorage.getItem(STORAGE_KEYS.messages);
    return {
      conversationId: convId || null,
      messages: msgs ? JSON.parse(msgs) : [],
    };
  } catch {
    return { conversationId: null, messages: [] };
  }
}

function persistSession(
  userId: string,
  conversationId: string | null,
  messages: ArchieChatMessage[]
) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.userId, userId);
    if (conversationId) {
      sessionStorage.setItem(STORAGE_KEYS.conversationId, conversationId);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.conversationId);
    }
    sessionStorage.setItem(STORAGE_KEYS.messages, JSON.stringify(messages));
  } catch {
    // ignore storage errors
  }
}

/* ---------- Thinking indicator ---------- */

function ThinkingIndicator() {
  const { t } = useTranslation(["archie"]);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: 1.5,
          py: 1,
          borderRadius: "12px 12px 12px 2px",
          backgroundColor: "var(--archie-bg)",
          border: "1px solid var(--archie-border)",
          fontSize: "0.8125rem",
          color: "var(--text-secondary)",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {t("archie:thinking")}
        <Box
          component="span"
          sx={{
            "@keyframes archieDots": {
              "0%, 20%": { content: '"."' },
              "40%": { content: '".."' },
              "60%, 100%": { content: '"..."' },
            },
            "&::after": {
              content: '""',
              animation: "archieDots 1.4s infinite steps(1)",
            },
          }}
        />
      </Box>
    </Box>
  );
}

/* ---------- Main component ---------- */

export default function ArchiePanel({ open, onClose }: ArchiePanelProps) {
  const { t } = useTranslation(["archie", "common"]);
  const formatRelativeTime = makeFormatRelativeTime(t as (key: string, opts?: Record<string, unknown>) => string);
  const { user } = useContext(AuthContext);
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("Chat");
  const [messages, setMessages] = useState<ArchieChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasRestoredRef = useRef(false);

  const startChat = useStartArchieChat();
  const continueChat = useContinueArchieChat();
  const loadConversation = useLoadArchieConversation();
  const invalidateConversations = useInvalidateArchieConversations();

  const conversationsQuery = useListArchieConversations(customerId);

  // Restore session on mount
  useEffect(() => {
    if (!user || hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    const session = restoreSession(user.userId);
    setConversationId(session.conversationId);
    setMessages(session.messages);
  }, [user]);

  // Persist session when it changes
  useEffect(() => {
    if (!user || !hasRestoredRef.current) return;
    persistSession(user.userId, conversationId, messages);
  }, [user, conversationId, messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isSending]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (open && activeTab === "Chat") {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open, activeTab]);

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || !customerId || isSending) return;

    const userMessage: ArchieChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsSending(true);

    try {
      if (conversationId) {
        const response = await continueChat.mutateAsync({
          conversationId,
          request: { userText: text, customerId, conversationId },
        });
        const assistantMessage: ArchieChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: response.assistantText,
          timestamp: new Date(),
          searchUrl: extractSearchUrl(response.assistantText, response.searchUrl),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        const response = await startChat.mutateAsync({
          userText: text,
          customerId,
        });
        setConversationId(response.conversationId);
        const assistantMessage: ArchieChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: response.assistantText,
          timestamp: new Date(),
          searchUrl: extractSearchUrl(response.assistantText, response.searchUrl),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        invalidateConversations(customerId);
      }
    } catch {
      const errorMessage: ArchieChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        text: t("archie:errorMessage"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText, customerId, isSending, conversationId, continueChat, startChat, invalidateConversations]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setConversationId(null);
    setMessages([]);
    setActiveTab("Chat");
  };

  const handleLoadConversation = async (convId: string) => {
    try {
      const response = await loadConversation.mutateAsync(convId);
      setConversationId(response.conversationId);
      setMessages(
        response.messages.map((msg) => ({
          id: msg.messageId,
          role: msg.role as "user" | "assistant",
          text: msg.text,
          timestamp: new Date(msg.createdAt),
          searchUrl:
            msg.role === "assistant"
              ? extractSearchUrl(msg.text)
              : undefined,
        }))
      );
      setActiveTab("Chat");
    } catch {
      // Silently fail — user can retry
    }
  };

  const handleViewResults = (url: string) => {
    // Transform V1 paths to V2: /customers/{id}/search → /customers/{id}/v2/search
    const v2Url = url.replace(/\/customers\/([^/]+)\//, "/customers/$1/v2/");
    navigate(v2Url);
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        width: ARCHIE_PANEL_WIDTH,
        flexShrink: 0,
        borderLeft: "1px solid var(--border-subtle)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        animation: "archieSlideIn 200ms ease-out",
        "@keyframes archieSlideIn": {
          from: { transform: "translateX(100%)", opacity: 0 },
          to: { transform: "translateX(0)", opacity: 1 },
        },
        backgroundColor: "var(--bg-surface)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          background: "var(--archie-gradient)",
          flexShrink: 0,
        }}
      >
        {/* Avatar */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 16, height: 16 }}
          >
            <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" />
            <circle cx="9" cy="13" r="1" />
            <circle cx="15" cy="13" r="1" />
          </Box>
        </Box>

        {/* Title block */}
        <div>
          <div
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
            }}
          >
            Archie
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.3,
            }}
          >
            {t("archie:aiAssistant")}
          </div>
        </div>

        {/* Close button */}
        <Box
          component="button"
          aria-label={t("common:close")}
          onClick={onClose}
          sx={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "none",
            background: "rgba(255,255,255,0.15)",
            color: "#fff",
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": {
              background: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            sx={{ width: 14, height: 14 }}
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid var(--border-subtle)",
          flexShrink: 0,
          backgroundColor: "var(--bg-surface)",
        }}
      >
        {TABS.map((tab) => (
          <Box
            key={tab}
            component="button"
            onClick={() => setActiveTab(tab)}
            sx={{
              flex: 1,
              py: 1.25,
              fontSize: "0.8125rem",
              fontWeight: activeTab === tab ? 600 : 400,
              color:
                activeTab === tab
                  ? "var(--archie-text)"
                  : "var(--text-secondary)",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === tab
                  ? "2px solid var(--archie-text)"
                  : "2px solid transparent",
              cursor: "pointer",
              transition: "color 150ms, border-color 150ms",
              "&:hover": {
                color: "var(--text-primary)",
              },
            }}
          >
            {t(`archie:${tab.toLowerCase()}`)}
          </Box>
        ))}
      </Box>

      {/* Chat tab content */}
      {activeTab === "Chat" && (
        <>
          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {messages.length === 0 && !isSending && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  textAlign: "center",
                  px: 3,
                  gap: 2,
                }}
              >
                {/* Onboarding avatar */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "var(--archie-gradient)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{ width: 24, height: 24 }}
                  >
                    <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" />
                    <circle cx="9" cy="13" r="1" />
                    <circle cx="15" cy="13" r="1" />
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontSize: "0.8125rem",
                    color: "var(--text-secondary)",
                    lineHeight: 1.6,
                  }}
                >
                  {t("archie:onboarding")}
                </Box>
              </Box>
            )}

            {messages.map((msg) => {
              const isUser = msg.role === "user";
              const displayText = isUser ? msg.text : stripBoilerplate(msg.text);
              const searchUrl = msg.searchUrl;

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "85%",
                      px: 1.5,
                      py: 1,
                      borderRadius: isUser
                        ? "12px 12px 2px 12px"
                        : "12px 12px 12px 2px",
                      backgroundColor: isUser
                        ? "var(--bg-active)"
                        : "var(--archie-bg)",
                      border: isUser
                        ? "1px solid var(--border-subtle)"
                        : "1px solid var(--archie-border)",
                      fontSize: "0.8125rem",
                      lineHeight: 1.6,
                      color: "var(--text-primary)",
                      "& p": { margin: 0 },
                      "& p + p": { mt: 1 },
                      "& ul, & ol": { my: 0.5, pl: 2.5 },
                      "& code": {
                        fontSize: "0.75rem",
                        backgroundColor: "var(--bg-raised)",
                        px: 0.5,
                        borderRadius: "4px",
                      },
                      "& a": {
                        color: "var(--archie-text)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {isUser ? (
                      displayText
                    ) : (
                      <Markdown
                        components={{
                          a: ({ href, children, ...props }) => (
                            <MarkdownLink
                              {...props}
                              href={href}
                              onViewResults={handleViewResults}
                            >
                              {children}
                            </MarkdownLink>
                          ),
                        }}
                      >
                        {displayText}
                      </Markdown>
                    )}
                  </Box>

                  {/* View Results chip */}
                  {searchUrl && !isUser && (
                    <Box
                      component="button"
                      onClick={() => handleViewResults(searchUrl)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.75,
                        px: 1.25,
                        py: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "var(--archie-text)",
                        background: "var(--archie-bg)",
                        border: "1px solid var(--archie-border)",
                        borderRadius: "20px",
                        cursor: "pointer",
                        "&:hover": {
                          background: "var(--archie-border)",
                        },
                      }}
                    >
                      <Box
                        component="svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        sx={{ width: 12, height: 12 }}
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </Box>
                      {t("archie:viewResults")}
                    </Box>
                  )}
                </Box>
              );
            })}

            {isSending && <ThinkingIndicator />}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input area */}
          <Box
            sx={{
              borderTop: "1px solid var(--border-subtle)",
              px: 2,
              py: 1.5,
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "flex-end",
              flexShrink: 0,
              backgroundColor: "var(--bg-surface)",
            }}
          >
            <Box
              component="textarea"
              ref={textareaRef}
              value={inputText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setInputText(e.target.value)
              }
              onKeyDown={handleKeyDown}
              placeholder={t("archie:askAnything")}
              rows={1}
              sx={{
                flex: 1,
                px: 1.5,
                py: 1,
                fontSize: "0.8125rem",
                fontFamily: "inherit",
                color: "var(--text-primary)",
                backgroundColor: "var(--bg-raised)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "8px",
                outline: "none",
                resize: "none",
                maxHeight: 100,
                lineHeight: 1.5,
                "&::placeholder": {
                  color: "var(--text-muted)",
                },
                "&:focus": {
                  borderColor: "var(--archie-border)",
                },
              }}
            />
            <Box
              component="button"
              aria-label={t("archie:send")}
              onClick={handleSend}
              disabled={!inputText.trim() || isSending}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "var(--archie-gradient)",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor:
                  !inputText.trim() || isSending ? "default" : "pointer",
                flexShrink: 0,
                opacity: !inputText.trim() || isSending ? 0.5 : 1,
                transition: "opacity 150ms",
                "&:hover": {
                  opacity: !inputText.trim() || isSending ? 0.5 : 0.85,
                },
              }}
            >
              <Box
                component="svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                sx={{ width: 14, height: 14 }}
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* History tab content */}
      {activeTab === "History" && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* New Chat button */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <Box
              component="button"
              onClick={handleNewChat}
              sx={{
                width: "100%",
                py: 1,
                px: 2,
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "var(--archie-text)",
                background: "var(--archie-bg)",
                border: "1px solid var(--archie-border)",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 150ms",
                "&:hover": {
                  background: "var(--archie-border)",
                },
              }}
            >
              {t("archie:newChat")}
            </Box>
          </Box>

          {/* Conversation list */}
          {conversationsQuery.isLoading && (
            <Box
              sx={{
                p: 2,
                fontSize: "0.8125rem",
                color: "var(--text-muted)",
                textAlign: "center",
              }}
            >
              {t("archie:loadingConversations")}
            </Box>
          )}

          {conversationsQuery.data?.conversations &&
            conversationsQuery.data.conversations.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                {t("archie:noPreviousConversations")}
              </Box>
            )}

          {conversationsQuery.data?.conversations?.map(
            (conv: ArchieConversationSummary) => (
              <Box
                key={conv.conversationId}
                component="button"
                onClick={() => handleLoadConversation(conv.conversationId)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  px: 2,
                  py: 1.5,
                  textAlign: "left",
                  background:
                    conv.conversationId === conversationId
                      ? "var(--bg-active)"
                      : "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--border-subtle)",
                  cursor: "pointer",
                  transition: "background 150ms",
                  "&:hover": {
                    background: "var(--bg-active)",
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {conv.title}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.6875rem",
                    color: "var(--text-muted)",
                  }}
                >
                  <span>{t("archie:messageCount", { count: conv.messageCount })}</span>
                  <span>{formatRelativeTime(conv.updatedAt)}</span>
                </Box>
              </Box>
            )
          )}
        </Box>
      )}
    </Box>
  );
}
